/**
 * Service layer — WBS templates + in-app milestone scaffolding.
 *
 * The phase milestone template lives in Supabase `wbs_templates` (migrated from Airtable). The
 * scaffolder creates a phase's milestones from that template — replacing the retired Airtable
 * "Create Milestones" automation. All access goes through Vercel API routes (no secret in the browser).
 * See docs/WBS_SCAFFOLDING.md.
 */

// Clerk-authed POST (mirrors services/phases.js). The scaffolder runs server-side (Airtable token +
// Supabase service role), so the browser only triggers it.
async function authedPost(path, body) {
  const token = window.__clerkGetToken ? await window.__clerkGetToken() : null
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body || {}),
  })
  let data = {}; try { data = await res.json() } catch (e) { data = {} }
  if (!res.ok && data && !data.error) data.error = 'Request failed: ' + res.status
  return data
}

/**
 * Scaffold a phase's milestones from the WBS template (on-demand, e.g. the per-phase button).
 * @param {object} p
 * @param {string} p.phaseRecordId  Airtable Phases record id
 * @param {string} [p.anchorDate]   YYYY-MM-DD start for the root milestone; server derives if omitted
 * @param {boolean} [p.force]       create even if the phase already has milestones (default false)
 * @returns {Promise<{created,skipped?,reason?,anchorDate?,milestoneIds?,phaseName?,error?}>}
 */
export function scaffoldPhaseMilestones({ phaseRecordId, anchorDate, force, templatePhase } = {}) {
  return authedPost('/api/scaffold-milestones', { phaseRecordId, anchorDate: anchorDate || null, force: !!force, templatePhase: templatePhase || null })
}

// ── WBS Template Editor (admin) ──────────────────────────────────────────────
// CRUD against Supabase wbs_templates via the admin-gated /api/wbs route.

/** All templates (grouped client-side by phase) + the Deliverable Types list for the picker. */
export function listWbsTemplates() {
  return authedPost('/api/wbs', { action: 'list' })
}
export function createWbsTemplate(fields) {
  return authedPost('/api/wbs', { action: 'create', ...fields })
}
export function updateWbsTemplate(id, fields) {
  return authedPost('/api/wbs', { action: 'update', id, ...fields })
}
export function deleteWbsTemplate(id) {
  return authedPost('/api/wbs', { action: 'delete', id })
}
